import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumberArray,
  FieldRadio,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY, VERTICAL_ALIGN_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { SplitterFormBrandType } from './splitterTypes'

const useShowIcon = ({ name }: IFieldConfig) => {
  const parent = useFormData<{ inversion?: boolean }>(name, 1)
  return useMemo(() => ({ hidden: parent?.inversion === false }), [parent?.inversion])
}

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.splitter.style, {
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  icon: {
    inversion: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('input.label.showIcon'),
    }),
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.splitter.schema.sliderIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
      useConfigs: [useShowIcon],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  splitter: {
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    width: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { showInput: true, min: 1, max: 50 },
    }),
  },
  textLeft: {
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        direction: 'horizontal',
      },
    }),
  },
  textRight: {
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        direction: 'horizontal',
      },
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.splitter.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.splitter.schema.innerSlideIndents'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
  },
})

const SplitterFormStyle: SplitterFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.splitter.tags.base')}</Typography>
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('elements.splitter.tags.splitter')}</Typography>
        <FormField config={schema.icon.inversion} />
        <FormField config={schema.icon.backgroundImage} />
        <FormField config={schema.icon.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.splitter.borderColor} />
        <FormField config={schema.splitter.width} />

        <Typography weight='medium'>{t('elements.splitter.tags.textLeft')}</Typography>
        <FormField config={schema.textLeft.alignItems} />

        <Typography weight='medium'>{t('elements.splitter.tags.textRight')}</Typography>
        <FormField config={schema.textRight.alignItems} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.splitter.tags.indents')}</Typography>
        <FormField config={schema.indents.padding} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(SplitterFormStyle) as typeof SplitterFormStyle
